import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { X, BarChart3, Calendar, Download, RefreshCw, TrendingUp, TrendingDown, Save, Eye, Trash2 } from "lucide-react";

const REPORT_TYPES = [
  { id: "collection_summary", label: "Collection Summary" },
  { id: "line_plan_status", label: "Line Plan Status" },
  { id: "sample_pipeline", label: "Sample Pipeline" },
  { id: "supplier_scorecard", label: "Supplier Scorecard" },
  { id: "quality_overview", label: "Quality Overview" },
  { id: "cost_analysis", label: "Cost Analysis" },
];

function getDatePreset(preset) {
  const today = new Date();
  const yyyy = today.getFullYear();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (preset === "last_week") {
    const dayOfWeek = today.getDay();
    const endOfLastWeek = new Date(today);
    endOfLastWeek.setDate(today.getDate() - dayOfWeek - 1);
    const startOfLastWeek = new Date(endOfLastWeek);
    startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
    return { from_date: fmt(startOfLastWeek), to_date: fmt(endOfLastWeek) };
  }
  if (preset === "last_month") {
    const firstOfThisMonth = new Date(yyyy, today.getMonth(), 1);
    const endOfLastMonth = new Date(firstOfThisMonth);
    endOfLastMonth.setDate(endOfLastMonth.getDate() - 1);
    const startOfLastMonth = new Date(endOfLastMonth.getFullYear(), endOfLastMonth.getMonth(), 1);
    return { from_date: fmt(startOfLastMonth), to_date: fmt(endOfLastMonth) };
  }
  if (preset === "last_quarter") {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const prevQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
    const prevYear = currentQuarter === 0 ? yyyy - 1 : yyyy;
    const startMonth = prevQuarter * 3;
    const start = new Date(prevYear, startMonth, 1);
    const end = new Date(prevYear, startMonth + 3, 0);
    return { from_date: fmt(start), to_date: fmt(end) };
  }
  if (preset === "ytd") {
    return { from_date: `${yyyy}-01-01`, to_date: fmt(today) };
  }
  return {};
}

function downloadCsv(columns, rows, filename) {
  const escape = (val) => {
    const str = val == null ? "" : String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  const header = columns.map(escape).join(",");
  const body = rows.map((row) => columns.map((col) => escape(row[col])).join(",")).join("\n");
  const csv = header + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const [selectedType, setSelectedType] = useState(REPORT_TYPES[0].id);
  const [report, setReport] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [filters, setFilters] = useState({ season: "", from_date: "", to_date: "" });
  const [scheduleForm, setScheduleForm] = useState({ report_type: "", frequency: "weekly", recipients: "" });
  const [saving, setSaving] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [viewingSaved, setViewingSaved] = useState(null);

  const loadSchedules = () => {
    api.get("/reports/schedules").then((r) => setSchedules(r.data)).catch(() => {});
  };

  const loadSavedReports = () => {
    api.get("/reports/saved").then((r) => setSavedReports(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadSchedules();
    loadSavedReports();
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/reports/generate", { report_type: selectedType, ...filters });
      setReport(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const saveReport = async () => {
    try {
      await api.post("/reports/saved", {
        report_type: selectedType,
        filters,
        report_data: report,
      });
      toast.success("Report saved");
      loadSavedReports();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteSavedReport = async (id) => {
    try {
      await api.delete(`/reports/saved/${id}`);
      toast.success("Saved report removed");
      loadSavedReports();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createSchedule = async () => {
    setSaving(true);
    try {
      await api.post("/reports/schedules", {
        ...scheduleForm,
        recipients: scheduleForm.recipients.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Report schedule created");
      setShowSchedule(false);
      setScheduleForm({ report_type: "", frequency: "weekly", recipients: "" });
      loadSchedules();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteSchedule = async (id) => {
    try {
      await api.delete(`/reports/schedules/${id}`);
      toast.success("Schedule removed");
      loadSchedules();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applyPreset = (preset) => {
    const dates = getDatePreset(preset);
    setFilters((f) => ({ ...f, ...dates }));
  };

  const displayReport = viewingSaved?.report_data || report;
  const displayType = viewingSaved?.report_type || selectedType;

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Reports & Analytics</h1>
          <p className="text-zinc-500 mt-2 text-sm">Generate reports, view key metrics and manage report schedules.</p>
        </div>
        <button onClick={() => setShowSchedule(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Calendar size={15} /> Schedule report
        </button>
      </div>

      {/* Report Type Selector + Filters */}
      <div className="mt-8 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Report type</label>
          <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}
            className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
            {REPORT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Season</label>
          <input value={filters.season} onChange={(e) => setFilters({ ...filters, season: e.target.value })} placeholder="e.g. SS26"
            className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 w-28" />
        </div>
        {/* Date range presets */}
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Quick range</label>
          <div className="flex gap-1">
            {[
              { key: "last_week", label: "Last Week" },
              { key: "last_month", label: "Last Month" },
              { key: "last_quarter", label: "Last Quarter" },
              { key: "ytd", label: "YTD" },
            ].map((p) => (
              <button key={p.key} onClick={() => applyPreset(p.key)}
                className="border border-zinc-300 rounded-md px-2.5 py-2.5 text-xs text-zinc-600 hover:bg-zinc-100 hover:border-zinc-400 transition-colors whitespace-nowrap">
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">From</label>
          <input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
        </div>
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">To</label>
          <input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
        </div>
        <button onClick={generate} disabled={loading}
          className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <BarChart3 size={14} />}
          {loading ? "Generating..." : "Generate"}
        </button>
      </div>

      {/* Viewing saved report banner */}
      {viewingSaved && (
        <div className="mt-6 flex items-center justify-between bg-zinc-100 border border-zinc-200 rounded-lg px-5 py-3">
          <p className="text-sm text-zinc-600">
            Viewing saved report: <span className="font-semibold">{REPORT_TYPES.find((t) => t.id === viewingSaved.report_type)?.label || viewingSaved.report_type}</span>
            {viewingSaved.created_at && <span className="text-zinc-400 ml-2">({new Date(viewingSaved.created_at).toLocaleDateString()})</span>}
          </p>
          <button onClick={() => setViewingSaved(null)} className="text-xs text-zinc-500 hover:text-zinc-800 font-medium">Close</button>
        </div>
      )}

      {/* Summary Cards */}
      {displayReport && (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {(displayReport.metrics || []).map((m, i) => (
              <div key={i} className="border border-zinc-200 bg-white rounded-lg p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{m.label}</p>
                <p className="text-2xl font-bold mt-1">{m.value}</p>
                {m.change != null && (
                  <div className={`flex items-center gap-1 mt-1 ${m.change > 0 ? "text-emerald-600" : m.change < 0 ? "text-red-600" : "text-zinc-400"}`}>
                    {m.change > 0 ? <TrendingUp size={14} /> : m.change < 0 ? <TrendingDown size={14} /> : null}
                    <span className="text-xs font-medium">{m.change > 0 ? "+" : ""}{m.change}%</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Report Data Table */}
          {(displayReport.rows || []).length > 0 && (
            <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
              <div className="px-6 py-3 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold">{REPORT_TYPES.find((t) => t.id === displayType)?.label}</h3>
                <div className="flex items-center gap-3">
                  {!viewingSaved && report && (
                    <button onClick={saveReport} className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1">
                      <Save size={12} /> Save Report
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const cols = displayReport.columns || [];
                      const rws = displayReport.rows || [];
                      const typeName = REPORT_TYPES.find((t) => t.id === displayType)?.label || displayType;
                      downloadCsv(cols, rws, `${typeName.replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`);
                    }}
                    className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"
                  >
                    <Download size={12} /> Export CSV
                  </button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100">
                    {(displayReport.columns || []).map((col) => (
                      <th key={col} className="text-left px-6 py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayReport.rows.map((row, i) => (
                    <tr key={i} className="border-b border-zinc-50">
                      {(displayReport.columns || []).map((col) => (
                        <td key={col} className="px-6 py-3">{row[col]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Saved Reports */}
      {savedReports.length > 0 && (
        <div className="mt-10">
          <h3 className="font-heading font-semibold text-sm mb-3">Saved Reports</h3>
          <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
            {savedReports.map((s) => (
              <div key={s.id} className="px-6 py-3 border-b border-zinc-50 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{REPORT_TYPES.find((t) => t.id === s.report_type)?.label || s.report_type}</p>
                  <p className="text-xs text-zinc-400">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                    {s.filters?.season ? ` | Season: ${s.filters.season}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setViewingSaved(s)} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 font-medium">
                    <Eye size={12} /> View
                  </button>
                  <button onClick={() => deleteSavedReport(s.id)} className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1 font-medium">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scheduled Reports */}
      {schedules.length > 0 && (
        <div className="mt-10">
          <h3 className="font-heading font-semibold text-sm mb-3">Scheduled Reports</h3>
          <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
            {schedules.map((s) => (
              <div key={s.id} className="px-6 py-3 border-b border-zinc-50 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{REPORT_TYPES.find((t) => t.id === s.report_type)?.label || s.report_type}</p>
                  <p className="text-xs text-zinc-400">{s.frequency} | {(s.recipients || []).join(", ")}</p>
                </div>
                <button onClick={() => deleteSchedule(s.id)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showSchedule && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowSchedule(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Schedule report</h2>
              <button onClick={() => setShowSchedule(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <select value={scheduleForm.report_type} onChange={(e) => setScheduleForm({ ...scheduleForm, report_type: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select report type</option>
                {REPORT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <select value={scheduleForm.frequency} onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              <input value={scheduleForm.recipients} onChange={(e) => setScheduleForm({ ...scheduleForm, recipients: e.target.value })} placeholder="Recipients (comma separated emails)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={createSchedule} disabled={!scheduleForm.report_type || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create schedule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
